import { useState, useEffect, useRef } from 'react';
import useApi from './../api';
import { toast } from 'react-toastify';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis, Label, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
import { conLog } from './../utils/conLog';
import { ArrowDownIcon, ArrowUpIcon, Clock, Maximize2Icon, BrainCircuit, Target, MessageSquare, Gauge, MapPin, Save, DollarSign, Lock, Loader2, Zap } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import AimdocLoader from '../ui/AimdocLoader';
import './map-styles.css';
import L from 'leaflet';
import 'leaflet.markercluster/dist/leaflet.markercluster.js';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { createRoot } from 'react-dom/client';
import { useUser } from '../user/UserContext';
import { LicenseType } from '../types';
import Upgrade from '../modals/Upgrade';
import ConversionFunnel from './ConversionFunnel';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from '@/components/ui/separator';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface AnalyticsData {
    total_messages_last_7_days: number;
    total_conversations_last_7_days: number;
    messages_per_day: {
        day: string;
        message_count: number;
    }[];
    conversations_per_day: {
        day: string;
        conversation_count: number;
    }[];
    messages_by_hour: {
        hour: number;
        message_count: number;
    }[];
    average_conversation_duration_minutes: number;
    previous_week?: {
        total_messages: number;
        total_conversations: number;
        average_messages_per_conversation: number;
        average_conversation_duration_minutes: number;
    };
    time_range?: string;
}

const timeRangeOptions = [
    { value: "24h", label: "Last 24 Hours" },
    { value: "7d", label: "Last Week" },
    { value: "30d", label: "Last Month" },
    { value: "90d", label: "Last Quarter" },
    { value: "365d", label: "Last Year" }
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const chartConfig = {
    messages: {
        label: "Messages",
        color: "hsl(210, 80%, 70%)"  // Light blue
    },
    conversations: {
        label: "Conversations",
        color: "hsl(210, 80%, 40%)"  // Dark blue
    },
    depth: {
        label: "Conversation Depth",
        color: "hsl(var(--primary))"
    },
    responseTime: {
        label: "Response Time",
        color: "#FF8042"  // Orange
    },
    topics: {
        label: "Topics",
        color: "#8884d8"  // Purple
    },
    activity: {
        label: "Activity",
        color: "#8884d8"  // Purple
    }
};

const chartStyles = {
    "--color-messages": chartConfig.messages.color,
    "--color-conversations": chartConfig.conversations.color,
    "--color-depth": chartConfig.depth.color,
    "--color-responseTime": chartConfig.responseTime.color,
    "--color-topics": chartConfig.topics.color,
    "--color-activity": chartConfig.activity.color
} as React.CSSProperties;

// Define Organization data interface (if not already defined elsewhere)
interface OrganizationData {
    id: string;
    name: string;
    revenue_per_lead: number;
    // Add other relevant fields if needed
}

// Define allowed license types
const ALLOWED_LICENSES = [LicenseType.PRO, LicenseType.BUSINESS, LicenseType.AIMDOC];

const AgentAnalytics = () => {
    const { get, put } = useApi();
    const { organization, loading, checkLicenseType } = useUser();
    const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
    const [timeRange, setTimeRange] = useState(() => {
        // Initialize time range from URL query param if present
        const params = new URLSearchParams(window.location.search);
        const rangeParam = params.get('range');
        const validRanges = ['24h', '7d', '30d', '90d', '365d'];
        return rangeParam && validRanges.includes(rangeParam) ? rangeParam : '7d';
    });
    const [activeTab, setActiveTab] = useState(() => {
        // Initialize tab from URL query param if present
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        const validTabs = ['conversion-funnel', 'engagement', 'performance', 'patterns', 'geographic'];
        return tabParam && validTabs.includes(tabParam) ? tabParam : 'conversion-funnel';
    });
    const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
    const [revenuePerLeadInput, setRevenuePerLeadInput] = useState<string>("");
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const [accessChecked, setAccessChecked] = useState<boolean>(false);
    const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);

    // Add new states for advanced analytics data
    const [conversationDepthData, setConversationDepthData] = useState<any[]>([]);
    const [engagementDistribution, setEngagementDistribution] = useState<any[]>([]);
    const [responseTimeTrend, setResponseTimeTrend] = useState<any[]>([]);
    const [performanceMetrics, setPerformanceMetrics] = useState<any>(null);
    const [topicDistribution, setTopicDistribution] = useState<any[]>([]);
    const [locationData, setLocationData] = useState<any[]>([]);
    const [revenueData, setRevenueData] = useState<any[]>([]);
    const [totalLeads, setTotalLeads] = useState<number>(0);
    const [revenuePerLead, setRevenuePerLead] = useState<number>(0);
    const [topQuestionsData, setTopQuestionsData] = useState<any[]>([]);
    const [topQuestionsLimit, setTopQuestionsLimit] = useState<number>(10);
    const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({
        basic: false,
        conversationDepth: false,
        engagement: false,
        response: false,
        performance: false,
        topic: false,
        location: false,
        revenue: false,
        topQuestions: false
    });

    useEffect(() => {
        if (!loading) {
            setHasAccess(checkLicenseType('pro'));
            setAccessChecked(true);
        }
    }, [organization, loading, checkLicenseType]);

    useEffect(() => {
        fetchAnalyticsData();
        fetchOrganizationData(); 
    }, [timeRange]);

    // Separate effect for top questions limit changes
    useEffect(() => {
        if (analyticsData) {
            fetchTopQuestionsData();
        }
    }, [topQuestionsLimit]);

    // Update URL when tab or time range changes
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set('tab', activeTab);
        params.set('range', timeRange);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
    }, [activeTab, timeRange]);

    const fetchOrganizationData = async () => {
        try {
            const response = await get('/users/me/organization');
            setOrganizationData(response.data);
            setRevenuePerLeadInput(response.data.revenue_per_lead?.toString() || "0");
        } catch (error) {
            toast.error('Error fetching organization data');
        }
    };

    const handleRevenueSave = async () => {
        if (!organizationData) return;

        const revenueValue = parseFloat(revenuePerLeadInput);
        if (isNaN(revenueValue)) {
            toast.error("Invalid revenue amount");
            return;
        }

        try {
            await put('/organizations/me/revenue', { revenue_per_lead: revenueValue });
            toast.success("Revenue per contact updated successfully!");
            setOrganizationData(prev => prev ? { ...prev, revenue_per_lead: revenueValue } : null);

            // Refresh revenue data with new revenue_per_lead value
            fetchRevenueData();
        } catch (error) {
            toast.error("Failed to update revenue per contact");
        }
    };

    const fetchAnalyticsData = async () => {
        setIsLoading(prev => ({ ...prev, basic: true }));
        try {
            const response = await get(`/analytics?time_range=${timeRange}`);
            conLog(response.data);
            setAnalyticsData(response.data);

            // Once basic data is loaded, fetch advanced analytics data
            fetchConversationDepthData();
            fetchEngagementDistributionData();
            fetchRevenueData();
            fetchPerformanceMetricsData();
            fetchTopicDistributionData();
            fetchLocationData();
            fetchTopQuestionsData();

        } catch (error) {
            toast.error('Error fetching analytics data');
        } finally {
            setIsLoading(prev => ({ ...prev, basic: false }));
        }
    };

    const fetchConversationDepthData = async () => {
        setIsLoading(prev => ({ ...prev, conversationDepth: true }));
        try {
            const response = await get(`/advanced/conversation-depth?time_range=${timeRange}`);
            setConversationDepthData(response.data.conversation_depth || []);
        } catch (error) {
            console.error('Error fetching conversation depth data:', error);
            // Fallback to derived data if API fails
            if (analyticsData) {
                const derived = analyticsData.messages_per_day.map((day) => {
                    const conversationData = analyticsData.conversations_per_day.find(c => c.day === day.day);
                    const conversationCount = conversationData?.conversation_count || 1;
                    const avgMessagesPerConversation = day.message_count / conversationCount;

                    return {
                        day: day.day,
                        avgMessagesPerConversation: avgMessagesPerConversation.toFixed(1)
                    };
                });
                setConversationDepthData(derived);
            }
        } finally {
            setIsLoading(prev => ({ ...prev, conversationDepth: false }));
        }
    };

    const fetchEngagementDistributionData = async () => {
        setIsLoading(prev => ({ ...prev, engagement: true }));
        try {
            const response = await get(`/advanced/engagement-distribution?time_range=${timeRange}`);
            setEngagementDistribution(response.data.engagement_distribution || []);
        } catch (error) {
            console.error('Error fetching engagement distribution data:', error);
            // Fallback to mock data if API fails
            if (analyticsData) {
                const mock = [
                    { name: 'High Engagement', value: Math.floor(analyticsData.total_conversations_last_7_days * 0.35) },
                    { name: 'Medium Engagement', value: Math.floor(analyticsData.total_conversations_last_7_days * 0.45) },
                    {
                        name: 'Low Engagement', value: Math.max(0, analyticsData.total_conversations_last_7_days -
                            Math.floor(analyticsData.total_conversations_last_7_days * 0.35) -
                            Math.floor(analyticsData.total_conversations_last_7_days * 0.45))
                    }
                ];
                setEngagementDistribution(mock);
            }
        } finally {
            setIsLoading(prev => ({ ...prev, engagement: false }));
        }
    };

    const fetchRevenueData = async () => {
        setIsLoading(prev => ({ ...prev, revenue: true }));
        try {
            const response = await get(`/advanced/revenue-data?time_range=${timeRange}`);
            const data = response.data;
            setRevenueData(data.revenue_data);
            setTotalLeads(data.total_leads);
            setRevenuePerLead(data.revenue_per_lead);
        } catch (error) {
            console.error('Error fetching revenue data:', error);
            setRevenueData([]);
            setTotalLeads(0);
            setRevenuePerLead(0);
        } finally {
            setIsLoading(prev => ({ ...prev, revenue: false }));
        }
    };

    const fetchPerformanceMetricsData = async () => {
        setIsLoading(prev => ({ ...prev, performance: true }));
        try {
            const response = await get(`/advanced/performance-metrics?time_range=${timeRange}`);
            setPerformanceMetrics(response.data || null);
        } catch (error) {
            console.error('Error fetching performance metrics data:', error);
            // Fallback to mock data
            setPerformanceMetrics({
                resolution_rate: Math.floor(65 + Math.random() * 20),
                first_response_time: (2 + Math.random() * 1.5).toFixed(1),
                messages_per_resolution: (5 + Math.random() * 3).toFixed(1)
            });
        } finally {
            setIsLoading(prev => ({ ...prev, performance: false }));
        }
    };


    const fetchTopicDistributionData = async () => {
        setIsLoading(prev => ({ ...prev, topic: true }));
        try {
            const response = await get(`/advanced/topic-distribution?time_range=${timeRange}`);
            setTopicDistribution(response.data.topic_distribution || []);
        } catch (error) {
            console.error('Error fetching topic distribution data:', error);
            // Fallback to mock data
            const mock = [
                { name: 'Product Info', value: Math.floor(20 + Math.random() * 30) },
                { name: 'Troubleshooting', value: Math.floor(15 + Math.random() * 25) },
                { name: 'Pricing', value: Math.floor(10 + Math.random() * 20) },
                { name: 'Features', value: Math.floor(10 + Math.random() * 15) },
                { name: 'Other', value: Math.floor(5 + Math.random() * 10) }
            ];
            setTopicDistribution(mock);
        } finally {
            setIsLoading(prev => ({ ...prev, topic: false }));
        }
    };


    const fetchLocationData = async () => {
        setIsLoading(prev => ({ ...prev, location: true }));
        try {
            const response = await get(`/advanced/location-distribution?time_range=${timeRange}`);
            setLocationData(response.data.location_distribution || []);
        } catch (error) {
            console.error('Error fetching location data:', error);
            // No fallback for location data as it's specific to our implementation
            setLocationData([]);
        } finally {
            setIsLoading(prev => ({ ...prev, location: false }));
        }
    };

    const fetchTopQuestionsData = async () => {
        setIsLoading(prev => ({ ...prev, topQuestions: true }));
        try {
            const response = await get(`/advanced/top-questions?time_range=${timeRange}&limit=${topQuestionsLimit}`);
            setTopQuestionsData(response.data.top_questions || []);
        } catch (error) {
            console.error('Error fetching top questions data:', error);
            setTopQuestionsData([]);
        } finally {
            setIsLoading(prev => ({ ...prev, topQuestions: false }));
        }
    };

    // Location Markers component for the map
    const LocationMarkers = ({ locations }: { locations: any[] }) => {
        const map = useMap();

        useEffect(() => {
            // Create marker cluster group
            const markerClusterGroup = L.markerClusterGroup();

            // Add all markers to the cluster group
            locations.forEach((location, index) => {
                const marker = L.marker([location.lat, location.lon]);

                // Create popup content
                let popupContent = '<div class="p-2">';

                if (location.city && location.country) {
                    popupContent += `<div class="font-medium mb-1">${location.city}, ${location.country}</div>`;
                } else if (location.city) {
                    popupContent += `<div class="font-medium mb-1">${location.city}</div>`;
                } else if (location.country) {
                    popupContent += `<div class="font-medium mb-1">${location.country}</div>`;
                }

                popupContent += `<div class="text-xs text-muted-foreground">
                    <strong>Coordinates</strong>: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}
                </div></div>`;

                marker.bindPopup(popupContent);
                markerClusterGroup.addLayer(marker);
            });

            // Add the cluster group to the map
            map.addLayer(markerClusterGroup);

            // Return cleanup function
            return () => {
                map.removeLayer(markerClusterGroup);
            };
        }, [locations, map]);

        return null;
    };

    // Show loading while we check access or while user/organization data is loading
    if (loading || !accessChecked) {
        return <AimdocLoader label="Loading agent analytics" />;
    }

    // Early return if user does not have access (only after we've checked)
    if (!hasAccess) {
        return (
            <div className="h-screen w-full flex flex-col p-6 bg-background">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Gauge className="h-6 w-6" />
                        Advanced Analytics
                    </h1>
                    <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Pro Feature</span>
                    </div>
                </div>

                {/* Blurred preview content */}
                <div className="h-full relative flex-1 overflow-hidden">
                    <div className="absolute inset-0 backdrop-blur-sm z-10 flex items-center justify-center">
                        <Card className="max-w-md mx-auto shadow-lg border-2">
                            <CardHeader className="text-center">
                                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                    <Gauge className="h-8 w-8 text-primary" />
                                </div>
                                <CardTitle className="text-xl">Advanced Analytics</CardTitle>
                            </CardHeader>
                            <CardContent className="text-center space-y-4">
                                <p className="text-muted-foreground">
                                    Get deep insights into your agent performance, user engagement, and business metrics.
                                </p>
                                <div className="space-y-2 text-sm text-left">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <span>Conversation depth analysis</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                                        <span>User engagement metrics</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                                        <span>Geographic visitor mapping</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                                        <span>Revenue & contact tracking</span>
                                    </div>
                                </div>
                                <Button 
                                    onClick={() => setUpgradeModalVisible(true)}
                                    className="w-full"
                                    size="lg"
                                >
                                    <Zap className="h-4 w-4 mr-2" />
                                    Upgrade to Engage
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    Available on Engage and Convert plans
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Blurred background content */}
                    <div className="blur-sm pointer-events-none opacity-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
                                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">1,234</div>
                                    <p className="text-xs text-muted-foreground">+12% from last week</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">2.3s</div>
                                    <p className="text-xs text-muted-foreground">-8% from last week</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Contact Conversion</CardTitle>
                                    <Target className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">34.2%</div>
                                    <p className="text-xs text-muted-foreground">+4% from last week</p>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">Revenue Generated</CardTitle>
                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">$12,345</div>
                                    <p className="text-xs text-muted-foreground">+25% from last week</p>
                                </CardContent>
                            </Card>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Message Trends</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[200px] bg-muted/20 rounded flex items-center justify-center">
                                        <div className="text-muted-foreground text-sm">Chart Preview</div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle>User Engagement</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-[200px] bg-muted/20 rounded flex items-center justify-center">
                                        <div className="text-muted-foreground text-sm">Chart Preview</div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>

                {/* Upgrade Modal */}
                <Upgrade 
                    showModal={upgradeModalVisible} 
                    setShowModal={setUpgradeModalVisible}
                    sessionId={null}
                />
            </div>
        );
    }

    if (!analyticsData) {
        return <AimdocLoader label="Loading agent analytics" />;
    }

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        if (timeRange === "24h") {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (timeRange === "7d") {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const formatHour = (hour: number) => {
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const adjustedHour = hour % 12 || 12;
        return `${adjustedHour}${ampm}`;
    };

    // User activity heatmap data
    const userActivityHeatmap = analyticsData.messages_by_hour.map(hour => ({
        hour: hour.hour,
        message_count: hour.message_count,
        intensity: Math.min(1, hour.message_count / (Math.max(...analyticsData.messages_by_hour.map(h => h.message_count)) || 1))
    }));

    return (
        <div 
            className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow"
            style={chartStyles}
        >
            <div className="flex justify-between items-center">
                <div>
                    <h1 className='text-4xl font-bold flex items-center gap-2'>Advanced Analytics</h1>
                    <p className='mt-2 text-sm text-muted-foreground'>In-depth analytics insights for your agents</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            placeholder="Revenue/Contact"
                            value={revenuePerLeadInput}
                            onChange={(e) => setRevenuePerLeadInput(e.target.value)}
                            className="w-[150px]"
                        />
                        <Button size="sm" variant="outline" onClick={handleRevenueSave} disabled={!organizationData}>
                            <Save className="h-4 w-4" />
                        </Button>
                    </div>
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select time range" />
                        </SelectTrigger>
                        <SelectContent>
                            {timeRangeOptions.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <Separator className="my-4" />

            {/* Loading state */}
            {isLoading.basic && !analyticsData && (
                <AimdocLoader label="Loading agent analytics" />
            )}

            {/* Advanced analytics tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="conversion-funnel">Conversion Funnel</TabsTrigger>
                    <TabsTrigger value="engagement">User Engagement</TabsTrigger>
                    <TabsTrigger value="performance">Performance</TabsTrigger>
                    <TabsTrigger value="patterns">Usage Patterns</TabsTrigger>
                    <TabsTrigger value="geographic">Geographic</TabsTrigger>
                </TabsList>

                <TabsContent value="conversion-funnel" className="mt-4">
                    <ConversionFunnel timeRange={timeRange} />
                </TabsContent>

                {/* User Engagement Tab */}
                <TabsContent value="engagement" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <MessageSquare className="h-5 w-5" />
                                        Conversation Depth Analysis
                                    </CardTitle>
                                    <CardDescription>Average messages per conversation over time</CardDescription>
                                </div>
                                {!isLoading.conversationDepth && conversationDepthData.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Average</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {(conversationDepthData.reduce((sum, item) =>
                                                    sum + parseFloat(item.avgMessagesPerConversation), 0) /
                                                    conversationDepthData.length).toFixed(1)} msgs
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isLoading.conversationDepth ? (
                                    <div className="h-[300px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : conversationDepthData.length === 0 ? (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[300px] w-full"
                                    >
                                        <LineChart data={conversationDepthData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                                dataKey="day"
                                                tickFormatter={formatDate}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                                minTickGap={32}
                                            />
                                            <YAxis tickLine={false} axisLine={false} />
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        labelFormatter={(value) => formatDate(value)}
                                                        formatter={(value) => [`${value} messages`, 'Avg Depth']}
                                                    />
                                                }
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="avgMessagesPerConversation"
                                                stroke="var(--color-depth)"
                                                strokeWidth={2}
                                                activeDot={{ r: 8 }}
                                                name="depth"
                                            />
                                        </LineChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Higher values indicate more engaging conversations
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <Target className="h-5 w-5" />
                                        Engagement Distribution
                                    </CardTitle>
                                    <CardDescription>Breakdown of conversation engagement levels</CardDescription>
                                </div>
                                {!isLoading.engagement && engagementDistribution.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Total Conversations</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {engagementDistribution.reduce((sum, item) => sum + item.value, 0)}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isLoading.engagement ? (
                                    <div className="h-[250px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : engagementDistribution.length === 0 ? (
                                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[250px] w-full"
                                    >
                                        <PieChart>
                                            <Pie
                                                data={engagementDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                                nameKey="name"
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {engagementDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        formatter={(value: any) => [`${value} conversations`, `${(Number(value) / analyticsData.total_conversations_last_7_days * 100).toFixed(1)}%`]}
                                                    />
                                                }
                                            />
                                        </PieChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Based on message count and conversation duration
                            </CardFooter>
                        </Card>

                        <Card className="lg:col-span-2">
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle>Message & Conversation Volume Comparison</CardTitle>
                                    <CardDescription>Daily comparison of messages and conversations</CardDescription>
                                </div>
                                {analyticsData && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Total Messages</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {analyticsData.total_messages_last_7_days}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {!analyticsData || !analyticsData.messages_per_day || analyticsData.messages_per_day.length === 0 ? (
                                    <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[400px] w-full"
                                    >
                                        <BarChart
                                            data={analyticsData.messages_per_day.map(message => {
                                                const conversation = analyticsData.conversations_per_day.find(
                                                    conv => conv.day === message.day
                                                );
                                                return {
                                                    day: message.day,
                                                    messages: message.message_count,
                                                    conversations: conversation?.conversation_count || 0
                                                };
                                            }).sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())}
                                            margin={{
                                                left: 12,
                                                right: 12,
                                            }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                                dataKey="day"
                                                tickFormatter={formatDate}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                                minTickGap={32}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                orientation="left"
                                                stroke="var(--color-messages)"
                                                label={{ value: 'Messages', angle: -90, position: 'insideLeft' }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                stroke="var(--color-conversations)"
                                                label={{ value: 'Conversations', angle: 90, position: 'insideRight' }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        labelFormatter={(value) => formatDate(value)}
                                                        formatter={(value, name) => [value, name === 'messages' ? 'Messages' : 'Conversations']}
                                                    />
                                                }
                                            />
                                            <Bar yAxisId="left" dataKey="messages" fill="var(--color-messages)" name="messages" radius={[4, 4, 0, 0]} />
                                            <Bar yAxisId="right" dataKey="conversations" fill="var(--color-conversations)" name="conversations" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Compare message and conversation trends to identify patterns
                            </CardFooter>
                        </Card>
                    </div>
                </TabsContent>

                {/* Performance Tab */}
                <TabsContent value="performance" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="h-5 w-5" />
                                        Revenue Trend
                                    </CardTitle>
                                    <CardDescription>Revenue trend over the period</CardDescription>
                                </div>
                                {!isLoading.revenue && revenueData.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Total Revenue</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                ${revenueData.reduce((sum, item) => sum + item.revenue, 0).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Total Contacts</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {totalLeads}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent className="relative">
                                {/* Coming Soon Overlay */}

                                {isLoading.revenue ? (
                                    <div className="h-[300px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : revenueData.length === 0 ? (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[300px] w-full"
                                    >
                                        <LineChart data={revenueData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                                dataKey="date"
                                                tickFormatter={formatDate}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                                minTickGap={32}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                tickLine={false}
                                                axisLine={false}
                                            >
                                                <Label value="Revenue ($)" angle={-90} position="insideLeft" />
                                            </YAxis>
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tickLine={false}
                                                axisLine={false}
                                            >
                                                <Label value="Contacts" angle={90} position="insideRight" />
                                            </YAxis>
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        labelFormatter={(value) => formatDate(value)}
                                                        formatter={(value, name) => {
                                                            if (name === 'revenue') {
                                                                return [`$${value}`, 'Revenue'];
                                                            }
                                                            return [value, 'Contacts'];
                                                        }}
                                                    />
                                                }
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="revenue"
                                                yAxisId="left"
                                                stroke="var(--color-responseTime)"
                                                strokeWidth={2}
                                                activeDot={{ r: 6 }}
                                                name="revenue"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="lead_count"
                                                yAxisId="right"
                                                stroke="var(--color-conversations)"
                                                strokeWidth={2}
                                                activeDot={{ r: 6 }}
                                                name="lead_count"
                                            />
                                        </LineChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Compare revenue trends to identify patterns
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <Gauge className="h-5 w-5" />
                                        Efficiency Metrics
                                    </CardTitle>
                                    <CardDescription>Key performance indicators for your agents</CardDescription>
                                </div>
                                {!isLoading.performance && performanceMetrics && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Resolution Rate</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {performanceMetrics.resolution_rate}%
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent className="relative">
                                {/* Coming Soon Overlay */}
                                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-b-lg">
                                    <div className="bg-primary/10 p-3 rounded-full mb-3">
                                        <Gauge className="h-8 w-8 text-primary" />
                                    </div>
                                    <h3 className="text-xl font-bold text-primary">Coming Soon</h3>
                                    <p className="text-sm text-muted-foreground max-w-[250px] text-center mt-2">
                                        AI-specific efficiency metrics are under development
                                    </p>
                                </div>

                                {isLoading.performance ? (
                                    <div className="h-[200px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : !performanceMetrics ? (
                                    <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">Resolution Rate</span>
                                                <span className="text-sm font-medium">{performanceMetrics.resolution_rate}%</span>
                                            </div>
                                            <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-muted">
                                                <div
                                                    className="h-2 rounded-full bg-green-500"
                                                    style={{ width: `${performanceMetrics.resolution_rate}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">First Response Time</span>
                                                <span className="text-sm font-medium">{performanceMetrics.first_response_time}s</span>
                                            </div>
                                            <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-muted">
                                                <div
                                                    className="h-2 rounded-full bg-blue-500"
                                                    style={{ width: `${Math.min(100, performanceMetrics.first_response_time * 10)}%` }}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">Messages per Resolution</span>
                                                <span className="text-sm font-medium">{performanceMetrics.messages_per_resolution}</span>
                                            </div>
                                            <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-muted">
                                                <div
                                                    className="h-2 rounded-full bg-purple-500"
                                                    style={{ width: `${Math.min(100, performanceMetrics.messages_per_resolution * 10)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Usage Patterns Tab */}
                <TabsContent value="patterns" className="mt-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <BrainCircuit className="h-5 w-5" />
                                        Topic Distribution
                                    </CardTitle>
                                    <CardDescription>Common conversation topics and intents</CardDescription>
                                </div>
                                {!isLoading.topic && topicDistribution.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Top Topic</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {topicDistribution.sort((a, b) => b.value - a.value)[0]?.name || 'None'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent className="relative">
                                {/* Coming Soon Overlay */}
                                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-b-lg">
                                    <div className="bg-primary/10 p-3 rounded-full mb-3">
                                        <BrainCircuit className="h-8 w-8 text-primary" />
                                    </div>
                                    <h3 className="text-xl font-bold text-primary">Coming Soon</h3>
                                    <p className="text-sm text-muted-foreground max-w-[250px] text-center mt-2">
                                        Advanced topic analysis is under development
                                    </p>
                                </div>

                                {isLoading.topic ? (
                                    <div className="h-[300px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : topicDistribution.length === 0 ? (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[300px] w-full"
                                    >
                                        <BarChart
                                            data={topicDistribution}
                                            layout="vertical"
                                            margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                            <XAxis
                                                type="number"
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                dataKey="name"
                                                type="category"
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        formatter={(value) => [`${value} conversations`, 'Count']}
                                                    />
                                                }
                                            />
                                            <Bar
                                                dataKey="value"
                                                fill="var(--color-topics)"
                                                barSize={20}
                                                radius={[0, 4, 4, 0]}
                                                name="topics"
                                            />
                                        </BarChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Based on intent classification
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="h-5 w-5" />
                                        User Activity Heatmap
                                    </CardTitle>
                                    <CardDescription>Activity distribution across hours of the day</CardDescription>
                                </div>
                                {analyticsData && analyticsData.messages_by_hour && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Peak Hour</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {formatHour(
                                                    analyticsData.messages_by_hour.reduce(
                                                        (maxHour, current) => current.message_count >
                                                            analyticsData.messages_by_hour[maxHour].message_count ?
                                                            current.hour : maxHour, 0
                                                    )
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {!analyticsData ? (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                        No data available
                                    </div>
                                ) : (
                                    <ChartContainer
                                        config={chartConfig}
                                        className="aspect-auto h-[300px] w-full"
                                    >
                                        <ScatterChart
                                            margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="hour"
                                                type="category"
                                                name="Hour"
                                                tickFormatter={formatHour}
                                                tickLine={false}
                                                axisLine={false}
                                                tickMargin={8}
                                            />
                                            <YAxis
                                                dataKey="message_count"
                                                type="number"
                                                name="Activity"
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <ZAxis type="number" dataKey="intensity" range={[50, 400]} />
                                            <ChartTooltip
                                                content={
                                                    <ChartTooltipContent
                                                        labelFormatter={hour => `Hour: ${formatHour(Number(hour))}`}
                                                        formatter={(value, name) => [value, name === 'message_count' ? 'Messages' : 'Hour']}
                                                    />
                                                }
                                            />
                                            <Scatter
                                                name="activity"
                                                data={userActivityHeatmap}
                                                fill="var(--color-activity)"
                                            />
                                        </ScatterChart>
                                    </ChartContainer>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Bubble size indicates relative activity volume
                            </CardFooter>
                        </Card>

                        {/* Top Asked Questions Card */}
                        <Card className="lg:col-span-2">
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <CardTitle className="flex items-center gap-2">
                                                <MessageSquare className="h-5 w-5" />
                                                Top Asked Questions
                                            </CardTitle>
                                            <CardDescription>Most frequently asked questions by users</CardDescription>
                                        </div>
                                        <Select value={topQuestionsLimit.toString()} onValueChange={(value) => setTopQuestionsLimit(parseInt(value))}>
                                            <SelectTrigger className="w-[120px]">
                                                <SelectValue placeholder="Show top..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="5">Top 5</SelectItem>
                                                <SelectItem value="10">Top 10</SelectItem>
                                                <SelectItem value="15">Top 15</SelectItem>
                                                <SelectItem value="20">Top 20</SelectItem>
                                                <SelectItem value="25">Top 25</SelectItem>
                                                <SelectItem value="30">Top 30</SelectItem>
                                                <SelectItem value="40">Top 40</SelectItem>
                                                <SelectItem value="50">Top 50</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {!isLoading.topQuestions && topQuestionsData.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Showing</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {topQuestionsData.length}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isLoading.topQuestions ? (
                                    <div className="h-[400px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : topQuestionsData.length === 0 ? (
                                    <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                                        No questions available
                                    </div>
                                ) : (
                                    <div className="px-6 py-4">
                                        <div className="overflow-hidden border rounded-lg">
                                            <table className="w-full table-auto">
                                                <thead>
                                                    <tr className="border-b bg-muted/50">
                                                        <th className="text-left py-3 px-4 font-medium text-sm w-16">Rank</th>
                                                        <th className="text-left py-3 px-4 font-medium text-sm">Question</th>
                                                        <th className="text-left py-3 px-4 font-medium text-sm w-24">Count</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {topQuestionsData.map((item, index) => (
                                                        <tr key={index} className="border-b hover:bg-muted/30 transition-colors">
                                                            <td className="py-3 px-4 text-sm text-muted-foreground">{index + 1}</td>
                                                            <td className="py-3 px-4 text-sm max-w-[600px] truncate" title={item.question}>
                                                                {item.question}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm">
                                                                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">
                                                                    {item.count}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Based on user message content in conversations
                            </CardFooter>
                        </Card>
                    </div>
                </TabsContent>

                {/* Geographic Tab */}
                <TabsContent value="geographic" className="mt-4">
                    <div className="grid grid-cols-1 gap-4">
                        <Card>
                            <CardHeader className="flex flex-col items-stretch space-y-0 border-b p-0 sm:flex-row">
                                <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
                                    <CardTitle className="flex items-center gap-2">
                                        <MapPin className="h-5 w-5" />
                                        Conversation Locations
                                    </CardTitle>
                                    <CardDescription>Geographical distribution of conversations based on visitor location</CardDescription>
                                </div>
                                {!isLoading.location && locationData.length > 0 && (
                                    <div className="flex">
                                        <div className="relative z-30 flex flex-1 flex-col justify-center gap-1 border-t border-l px-6 py-4 text-left bg-muted/50 sm:border-l sm:border-t-0 sm:px-8 sm:py-6">
                                            <span className="text-xs text-muted-foreground">Total Locations</span>
                                            <span className="text-lg font-bold leading-none sm:text-2xl">
                                                {locationData.length}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {isLoading.location ? (
                                    <div className="h-[500px] flex items-center justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin" />
                                    </div>
                                ) : locationData.length === 0 ? (
                                    <div className="h-[500px] flex items-center justify-center text-muted-foreground">
                                        No location data available
                                    </div>
                                ) : (
                                    <div className="h-[500px] w-full rounded-md overflow-hidden">
                                        <MapContainer
                                            center={[20, 0]}
                                            zoom={2}
                                            style={{ height: '100%', width: '100%' }}
                                            scrollWheelZoom={false}
                                        >
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <LocationMarkers locations={locationData} />
                                        </MapContainer>
                                    </div>
                                )}
                            </CardContent>
                            <CardFooter className="text-sm text-muted-foreground">
                                Based on IP geolocation of visitor sessions
                            </CardFooter>
                        </Card>
                    </div>
                </TabsContent>

            </Tabs>
        </div>
    );
};

export default AgentAnalytics;